import { Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Stack, Button, Paper, Typography, Box } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded'
import BlockRoundedIcon from '@mui/icons-material/BlockRounded'

import HeartbeatLoader from '@/components/ui/loaders/HeartbeatLoader'
import { useGetTimeTrackingAdminStaffsQuery, useGetTimeTrackingSessionsQuery } from '@/core/api'

import { useTimeTrackingFilters } from '../model/useTimeTrackingFilters'
import { useTimeTrackingExport } from '../model/useTimeTrackingExport'
import { useSessionDialogs } from '../model/useSessionDialogs'

import { AdminSessionsFilter } from './AdminSessionsFilter'
import { SessionsTable } from './SessionsTable'
import { CorrectSessionDialog } from './CorrectSessionDialog'
import { AdminCreateSessionDialog } from './AdminCreateSessionDialog'
import { AuditLogDialog } from './AuditLogDialog'

type AdminTimeTrackingDashboardProps = {
  networkId?: string
}

export function AdminTimeTrackingDashboard({ networkId }: AdminTimeTrackingDashboardProps) {
  useLingui()

  const { from, to, selectedStaffId, pagination, setFrom, setTo, setSelectedStaffId, setPagination } = useTimeTrackingFilters()
  const handleExport = useTimeTrackingExport({ from, to, staffId: selectedStaffId, networkId })
  const {
    correctSession,
    openCorrectDialog,
    closeCorrectDialog,
    historySessionId,
    openHistoryDialog,
    closeHistoryDialog,
    createDialogOpen,
    openCreateDialog,
    closeCreateDialog,
  } = useSessionDialogs()

  const { data: adminStaffs = [], isLoading: isLoadingStaffs, isError: isStaffsError, error: staffsError } = useGetTimeTrackingAdminStaffsQuery(
    networkId ? { network: networkId } : undefined
  )

  const { data, isFetching } = useGetTimeTrackingSessionsQuery({
    from: from.toISOString(),
    to: to.toISOString(),
    skip: pagination.pageIndex * pagination.pageSize,
    limit: pagination.pageSize,
    ...(selectedStaffId && { staff: selectedStaffId }),
    ...(networkId && { network: networkId }),
  })

  if (isLoadingStaffs) {
    return (
      <Stack flex={1} alignItems="center" justifyContent="center" sx={{ minHeight: '50vh' }}>
        <HeartbeatLoader sx={{ transform: 'scale(4)' }} />
      </Stack>
    )
  }

  if (isStaffsError) {
    const statusCode = staffsError && 'status' in staffsError ? staffsError.status : undefined
    const isForbidden = statusCode === 403

    return (
      <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ minHeight: '50vh' }}>
        <BlockRoundedIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {isForbidden ? <Trans>Access restricted</Trans> : <Trans>Something went wrong</Trans>}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 360 }}>
          {isForbidden ? (
            <Trans>You do not have permission to manage time tracking for this scope. Contact your administrator if you believe this is an error.</Trans>
          ) : (
            <Trans>Unable to load time tracking data. Please try again later.</Trans>
          )}
        </Typography>
      </Stack>
    )
  }

  return (
    <Box sx={{ bgcolor: 'grey.50', minHeight: '100%' }}>
      <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 3, md: 4 } }}>
        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'flex-start' }} justifyContent="space-between" spacing={2}>
            <Stack spacing={0.5}>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: -0.5 }}>
                <Trans>Time Tracking</Trans>
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <Trans>Review and manage working hours of your staff</Trans>
                </Typography>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FileDownloadRoundedIcon />}
                onClick={handleExport}
                sx={{ height: 44, px: 2.5, textTransform: 'none', fontWeight: 600, borderRadius: 1.5, whiteSpace: 'nowrap' }}
              >
                <Trans>Export CSV</Trans>
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddRoundedIcon />}
                onClick={openCreateDialog}
                sx={{
                  height: 44,
                  px: 2.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 1.5,
                  boxShadow: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': { boxShadow: '0 4px 12px rgba(0, 46, 63, 0.18)' },
                }}
              >
                <Trans>Add session</Trans>
              </Button>
            </Stack>
          </Stack>

          <AdminSessionsFilter
            from={from}
            to={to}
            staffs={adminStaffs}
            selectedStaffId={selectedStaffId}
            onFromChange={setFrom}
            onToChange={setTo}
            onStaffChange={setSelectedStaffId}
          />

          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 2, overflow: 'hidden' }}>
            <SessionsTable
              key={`${networkId ?? ''}-${from.toISOString()}-${to.toISOString()}-${selectedStaffId ?? ''}`}
              sessions={data?.items ?? []}
              loading={isFetching}
              totalRows={data?.totalCount ?? 0}
              onPaginationChange={setPagination}
              onCorrect={openCorrectDialog}
              onViewHistory={openHistoryDialog}
              isAdminView
            />
          </Paper>
        </Stack>

        <CorrectSessionDialog open={!!correctSession} session={correctSession} isAdminView onClose={closeCorrectDialog} />
        <AuditLogDialog sessionId={historySessionId} onClose={closeHistoryDialog} />
        <AdminCreateSessionDialog open={createDialogOpen} staffs={adminStaffs} onClose={closeCreateDialog} />
      </Box>
    </Box>
  )
}
